import { useState, useEffect } from 'react';
import nhost from '../nhost';
import ImageUpload from './ImageUpload';

const MEMBERS_QUERY = `query { profiles(where: { approved: { _eq: true } }) { id display_name } }`;
const SESSIONS_QUERY = `query { sessions(order_by: { created_at: desc }) { id food profile { id display_name } } }`;

export default function WineModal({ profile, wines, sessionId: initialSessionId, eventId: initialEventId, onClose, onSaved }) {
  const [members, setMembers] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [selectedHost, setSelectedHost] = useState(profile.id);
  const [sessionId, setSessionId] = useState(initialSessionId || null);

  const [name, setName] = useState('');
  const [grape, setGrape] = useState('');
  const [type, setType] = useState('Rött');
  const [country, setCountry] = useState('');
  const [region, setRegion] = useState('');
  const [vintage, setVintage] = useState('');
  const [food, setFood] = useState('');
  const [profileSource, setProfileSource] = useState('');
  const [profileText, setProfileText] = useState('');
  const [star, setStar] = useState(0);
  const [comment, setComment] = useState('');
  const [price, setPrice] = useState('');
  const [packaging, setPackaging] = useState('');

  const [imageUrl, setImageUrl] = useState(null);
  const [url, setUrl] = useState('');
  const [urlStatus, setUrlStatus] = useState('');
  const [urlLoading, setUrlLoading] = useState(false);
  const [duplicates, setDuplicates] = useState([]);
  const [saving, setSaving] = useState(false);
  const [selectedExistingWine, setSelectedExistingWine] = useState(null);

  useEffect(() => {
    nhost.graphql.request(MEMBERS_QUERY).then(({ data }) => { if (data) setMembers(data.profiles); });
    nhost.graphql.request(SESSIONS_QUERY).then(({ data }) => { if (data) setSessions(data.sessions); });
  }, []);

  function checkDuplicate(val) {
    if (val.length < 3) { setDuplicates([]); return; }
    const matches = wines.filter(w => w.name.toLowerCase().includes(val.toLowerCase()));
    const unique = [...new Map(matches.map(w => [w.name, w])).values()];
    setDuplicates(unique);
  }

  function useExistingWine(w) {
    setName(w.name || '');
    setGrape(w.grape || '');
    if (w.type) setType(w.type);
    setCountry(w.country || '');
    setRegion(w.region || '');
    if (w.vintage) setVintage(String(w.vintage));
    setProfileSource(w.profile_source || '');
    setProfileText(w.profile || '');
    if (w.price) setPrice(String(w.price));
    setPackaging(w.packaging || '');
    setSelectedExistingWine(w);
    setDuplicates([]);
  }

  async function fetchFromUrl() {
    if (!url.trim()) return;
    setUrlLoading(true);
    setUrlStatus('loading');
    try {
      const resp = await fetch('/api/fetch-wine', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: url.trim() })
      });
      const wine = await resp.json();
      if (wine?.name) {
        setName(wine.name || '');
        setGrape(wine.grape || '');
        if (wine.type) setType(wine.type);
        setCountry(wine.country || '');
        setRegion(wine.region || '');
        if (wine.vintage) setVintage(wine.vintage);
        setProfileSource(wine.profileSource || '');
        setProfileText(wine.profile || '');
        if (wine.price) setPrice(wine.price);
        if (wine.packaging) setPackaging(wine.packaging);
        if (wine.image_url) setImageUrl(wine.image_url);
        setUrlStatus('success');
        checkDuplicate(wine.name || '');
      } else throw new Error('no name');
    } catch {
      setUrlStatus('error');
    }
    setUrlLoading(false);
  }

  async function save() {
    if (!name.trim()) { alert('Ange vinets namn'); return; }
    setSaving(true);

    let sid = sessionId || null;
    let wineId;

    if (selectedExistingWine) {
      wineId = selectedExistingWine.id;
    } else {
      const wineObj = {
        name: name.trim(),
        grape: grape || null,
        type,
        country: country || null,
        region: region || null,
        vintage: vintage ? parseInt(vintage) : null,
        profile: profileText || null,
        profile_source: profileSource || null,
        price: price ? parseInt(price) : null,
        packaging: packaging || null,
        created_by: profile.id,
        image_url: imageUrl || null,
        source_url: url.trim() || null,
      };
      if (sid) {
        wineObj.session_id = sid;
      } else if (initialEventId) {
        wineObj.event_id = initialEventId;
      }

      const { data } = await nhost.graphql.request(`
        mutation AddWine($obj: wines_insert_input!) {
          insert_wines_one(object: $obj) { id }
        }
      `, { obj: wineObj });
      wineId = data?.insert_wines_one?.id;
    }

    if (selectedExistingWine) {
      await nhost.graphql.request(`
        mutation IncrementUseCount($id: uuid!) {
          update_wines(where: { id: { _eq: $id } }, _inc: { use_count: 1 }) { affected_rows }
        }
      `, { id: selectedExistingWine.id });
    }

    if (star && wineId) {
      await nhost.graphql.request(`
        mutation AddRating($wine_id: uuid!, $user_id: uuid!, $rating: numeric!, $comment: String) {
          insert_ratings_one(object: { wine_id: $wine_id, user_id: $user_id, rating: $rating, comment: $comment },
            on_conflict: { constraint: ratings_wine_id_user_id_key, update_columns: [rating, comment] }) { id }
        }
      `, { wine_id: wineId, user_id: profile.id, rating: star, comment: comment || null });
    }

    // Meddela övriga medlemmar via push – men bara för lösa viner (varken
    // kopplade till en provningssession eller en vinlåda), inte när ett
    // befintligt vin återanvänds, och aldrig till den som lade till det.
    if (!selectedExistingWine && wineId && !sid && !initialEventId) {
      notifyOthersAboutNewWine(name.trim(), wineId);
    }

    setSaving(false);
    onSaved();
  }

  async function notifyOthersAboutNewWine(wineName, newWineId) {
    try {
      const subsResp = await fetch('/api/push?action=send').then(r => r.json());
      const recipientIds = (subsResp?.subscribedUserIds || []).filter(id => id !== profile.id);
      if (recipientIds.length === 0) return;
      await fetch('/api/push?action=send', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: '🍷 Nytt vin tillagt',
          body: `${profile.display_name} lade till ${wineName} i vinlistan`,
          url: '/',
          data: { type: 'new_wine', wineId: newWineId },
          userIds: recipientIds,
        }),
      });
    } catch {}
  }

  return (
    <div className="modal-bg" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <div className="modal-handle" />
        <div className="modal-title">Lägg till vin</div>

        <div className="field">
          <label>Importera från länk</label>
          <div className="url-row">
            <input className="url-input" value={url} onChange={e => setUrl(e.target.value)} placeholder="Systembolaget, Vivino, Kobaj, Dittvin, Supervin..." />
            <button className="url-btn" onClick={fetchFromUrl} disabled={urlLoading}>{urlLoading ? '...' : 'Hämta'}</button>
          </div>
          {urlStatus === 'loading' && <div className="url-status loading">Hämtar information...</div>}
          {urlStatus === 'success' && (
            <>
              <div className="url-status success">✓ Informationen hämtades!</div>
              <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 4 }}>ℹ Kontrollera att all information stämmer innan du sparar – vissa fält kan saknas eller vara felaktiga.</div>
            </>
          )}
          {urlStatus === 'error' && <div className="url-status error">Kunde inte hämta. Fyll i manuellt.</div>}
        </div>

        <div className="divider"><div className="divider-line" /><div className="divider-text">eller fyll i manuellt</div><div className="divider-line" /></div>

        {selectedExistingWine && (
          <div style={{ background: 'var(--teal-light)', borderRadius: 8, padding: '10px 14px', marginBottom: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--teal)' }}>✓ Använder befintligt vin</div>
              <div style={{ fontSize: 12, color: 'var(--muted)' }}>{selectedExistingWine.name}{selectedExistingWine.vintage ? ` (${selectedExistingWine.vintage})` : ''}</div>
            </div>
            <button className="btn-secondary" style={{ fontSize: 11, padding: '3px 10px' }} onClick={() => setSelectedExistingWine(null)}>Ångra</button>
          </div>
        )}

        {duplicates.length > 0 && (
          <div className="dup-warning">
            <div className="dup-title">⚠ Det här vinet finns redan i databasen</div>
            <div className="dup-text">Vill du använda ett befintligt vin eller fortsätta fylla i manuellt?</div>
            {duplicates.map(w => {
              const count = wines.filter(x => x.name === w.name).length;
              return (
                <div key={w.id} className="dup-match" style={{ alignItems: 'center' }}>
                  <div>
                    <div>{w.name}{w.vintage ? ` (${w.vintage})` : ''}</div>
                    <div style={{ fontSize: 11, color: 'var(--muted)' }}>Provats {count}× · {w.region || 'okänd region'}</div>
                  </div>
                  <button
                    className="btn-secondary"
                    style={{ fontSize: 11, padding: '3px 10px', flexShrink: 0 }}
                    onClick={() => useExistingWine(w)}
                  >
                    Använd detta vin
                  </button>
                </div>
              );
            })}
            <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 8 }}>Eller fortsätt fylla i manuellt nedan.</div>
          </div>
        )}

        <div className="field">
          <label>Vinets namn</label>
          <input value={name} onChange={e => { setName(e.target.value); checkDuplicate(e.target.value); }} placeholder="t.ex. Château Margaux 2018" />
        </div>
        <div className="field"><label>Druva</label><input value={grape} onChange={e => setGrape(e.target.value)} placeholder="t.ex. Cabernet Sauvignon" /></div>
        <div className="field">
          <label>Vintyp</label>
          <select value={type} onChange={e => setType(e.target.value)}>
            <option>Rött</option><option>Vitt</option><option>Rosé</option><option>Mousserande</option><option>Dessertvin</option>
          </select>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <div className="field" style={{ marginBottom: 0 }}>
            <label>Land</label>
            <input value={country} onChange={e => setCountry(e.target.value)} placeholder="t.ex. Frankrike" />
          </div>
          <div className="field" style={{ marginBottom: 0 }}>
            <label>Region</label>
            <input value={region} onChange={e => setRegion(e.target.value)} placeholder="t.ex. Bordeaux" />
          </div>
        </div>
        <div style={{ marginBottom: 14 }} />
        <div className="field"><label>Årgång</label><input type="number" value={vintage} onChange={e => setVintage(e.target.value)} placeholder="t.ex. 2019" /></div>
        <div className="field"><label>Pris (kr)</label><input type="number" value={price} onChange={e => setPrice(e.target.value)} placeholder="t.ex. 149" /></div>
        <div className="field"><label>Förpackning</label><input value={packaging} onChange={e => setPackaging(e.target.value)} placeholder="t.ex. Glasflaska, Tetra, PET-flaska" /></div>

        {initialSessionId && !initialEventId && (
          <>
            <div className="field"><label>Mat som serverades</label><input value={food} onChange={e => setFood(e.target.value)} placeholder="t.ex. Lammkotlett" /></div>
            <div className="field">
              <label>Vem höll i provningen?</label>
              <div className="host-grid">
                {members.map(m => (
                  <div key={m.id} className={`host-btn ${selectedHost === m.id ? 'selected' : ''}`} onClick={() => setSelectedHost(m.id)}>
                    {m.display_name}
                  </div>
                ))}
              </div>
            </div>
          </>
        )}

        <div className="field">
          <label>Smakprofil – källa</label>
          <select value={profileSource} onChange={e => setProfileSource(e.target.value)}>
            <option value="">Välj källa...</option>
            <option>Systembolaget</option><option>Vivino</option><option>Winefinder</option>
            <option>Producenten</option><option>Wine Spectator</option><option>Annan källa</option>
          </select>
        </div>
        <div className="field">
          <label>Smakbeskrivning</label>
          <textarea value={profileText} onChange={e => setProfileText(e.target.value)} placeholder="Klistra in eller skriv smakprofilen..." />
        </div>

        <div className="field">
          <label>Bild på vinet</label>
          <ImageUpload
            currentUrl={imageUrl}
            onUploaded={setImageUrl}
            size={80}
            shape="rounded"
            label="Lägg till bild"
            showCamera
          />
        </div>

        <div className="field">
          <label>Ditt betyg</label>
          <div style={{ display: 'flex', justifyContent: 'center', gap: 2, padding: '4px 0' }}>
            {[1,2,3,4,5].map(pos => {
              const isFull = star >= pos;
              const isHalf = !isFull && star >= pos - 0.5;
              return (
                <div key={pos} style={{ position: 'relative', width: 44, height: 44, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <span style={{ fontSize: 36, color: (isFull || isHalf) ? 'var(--wine)' : '#ddd', opacity: isHalf ? 0.5 : 1, userSelect: 'none', pointerEvents: 'none' }}>★</span>
                  <div style={{ position: 'absolute', inset: 0, display: 'flex', cursor: 'pointer' }}>
                    <div style={{ flex: 1 }} onClick={() => setStar(pos - 0.5)} />
                    <div style={{ flex: 1 }} onClick={() => setStar(pos)} />
                  </div>
                </div>
              );
            })}
          </div>
          {star > 0 && <div style={{ textAlign: 'center', fontSize: 13, color: 'var(--muted)', marginTop: 2 }}>{star} / 5</div>}
        </div>
        <div className="field"><label>Din kommentar</label><textarea value={comment} onChange={e => setComment(e.target.value)} placeholder="Dina smaknoter..." /></div>

        <div className="btn-row">
          <button className="btn-secondary" onClick={onClose}>
            {initialSessionId ? 'Lägg till viner senare' : 'Avbryt'}
          </button>
          <button className="btn-primary" onClick={save} disabled={saving}>{saving ? 'Sparar...' : 'Spara vin'}</button>
        </div>
      </div>
    </div>
  );
}
